import { AuthService } from 'app/store/services/auth.service';
import { UserService } from 'app/store/services/user.service';
import { UserState } from 'app/store/models/user.model';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { setBannerComponent } from 'app/store/actions/page.action';
import { getIsAuthenticated } from 'app/store/selectors/user.selector';
import { IReducers } from '../../../../store/reducers';

declare const google: any;

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit, OnDestroy, AfterViewInit {
  loginForm: FormGroup;
  showAlert: boolean;
  alertMessage: string;
  loginInProgress: boolean;
  showSsoSection = false;
  showGoogleButton = false;
  showSpacescoutButton = false;
  private continueUrl: string | null = null;
  private googleClientId = '';
  private spacescoutClientId = '';
  private spacescoutAuthorizeUrl = '';
  private googleReady = false;

  @ViewChild('googleBtn', { static: false }) googleBtn: ElementRef;

  private destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private store: Store<IReducers>,
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private userService: UserService,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
  ) {
    this.store.dispatch(setBannerComponent({ banner: 'Login' }));
    this.continueUrl = this.route.snapshot.queryParamMap.get('continue');
  }

  get username(): AbstractControl {
    return this.loginForm.get('username');
  }

  get password(): AbstractControl {
    return this.loginForm.get('password');
  }

  onSubmit = () => {
    if (this.loginForm.invalid) {
      return;
    }

    this.loginInProgress = true;

    this.authService.login(this.username.value, this.password.value);
  };

  onKeyDown(event: any) {
    if (event.keyCode === 13) {
      this.onSubmit();
    }
  }

  ngOnInit() {
    this.loginForm = new FormGroup({
      username: new FormControl('', [
        Validators.required,
        Validators.minLength(4),
      ]),
      password: new FormControl('', [
        Validators.required,
        Validators.minLength(6),
      ]),
    });

    this.store.select('user').subscribe((state: UserState) => {
      if (state.redirectRequired === true && state.redirectTo) {
        this.router.navigate(['/' + state.redirectTo.route], {
          queryParams: state.redirectTo.params,
        });
      }
      if (state.status && state.status === 'error' && state.error) {
        this.loginInProgress = false;
        this.showAlert = true;
        this.alertMessage = state.error.error.message;
        setTimeout(() => this.closeAlert(), 5000);
      }
    });

    this.store
      .select(getIsAuthenticated)
      .subscribe((isAuthenticated: boolean) => {
        if (isAuthenticated) {
          if (this.continueUrl) {
            const token = JSON.parse(localStorage.getItem('token') || '{}');
            const accessToken = token?.data?.access_token || token?.access_token || '';
            const sep = this.continueUrl.includes('?') ? '&' : '?';
            window.location.href = this.continueUrl + sep + 'access_token=' + accessToken;
            return;
          }
          localStorage.setItem('comming_from', 'login');
          this.router.navigate(['/messages']);
        }
      });

    this.userService
      .getSsoConfig()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (config) => {
          const enabled = Array.isArray(config?.enabled_providers)
            ? config.enabled_providers
            : [];
          this.googleClientId = (config?.google?.client_id || '').trim();
          this.spacescoutClientId = (config?.spacescout?.client_id || '').trim();
          this.spacescoutAuthorizeUrl = (config?.spacescout?.authorize_url || '').trim();

          this.showGoogleButton =
            enabled.includes('google') && this.googleClientId !== '';
          this.showSpacescoutButton =
            enabled.includes('spacescout') &&
            this.spacescoutClientId !== '' &&
            this.spacescoutAuthorizeUrl !== '';
          this.showSsoSection = this.showGoogleButton || this.showSpacescoutButton;
          this.cdr.detectChanges();

          if (this.showGoogleButton && this.googleReady) {
            this.initGoogleButton();
          }
        },
        error: () => {
          this.showSsoSection = false;
          this.showGoogleButton = false;
          this.showSpacescoutButton = false;
        },
      });
  }

  ngAfterViewInit(): void {
    this.googleReady = true;
    if (this.showGoogleButton) {
      this.initGoogleButton();
    }
  }

  private initGoogleButton(attempt = 0): void {
    if (!this.showGoogleButton || !this.googleClientId) {
      return;
    }
    if (typeof google !== 'undefined' && google?.accounts?.id) {
      google.accounts.id.initialize({
        client_id: this.googleClientId,
        ux_mode: 'popup',
        callback: (response: any) => {
          this.ngZone.run(() => {
            this.authService.loginWithSso('google', { id_token: response.credential });
          });
        },
      });
      if (this.googleBtn?.nativeElement) {
        this.googleBtn.nativeElement.innerHTML = '';
        google.accounts.id.renderButton(this.googleBtn.nativeElement, {
          theme: 'outline',
          size: 'large',
          width: 280,
          type: 'standard',
          text: 'signin_with',
          shape: 'rectangular',
          logo_alignment: 'left',
        });
      }
      return;
    }
    if (attempt < 50) {
      setTimeout(() => this.initGoogleButton(attempt + 1), 100);
    }
  }

  loginWithSpacescout(): void {
    if (!this.spacescoutAuthorizeUrl || !this.spacescoutClientId) {
      return;
    }
    const state = Math.random().toString(36).substring(2);
    const codeVerifier = this.generateCodeVerifier();
    sessionStorage.setItem('sso_state', state);
    sessionStorage.setItem('sso_code_verifier', codeVerifier);

    this.generateCodeChallenge(codeVerifier).then(challenge => {
      const params = new URLSearchParams({
        client_id: this.spacescoutClientId,
        response_type: 'code',
        redirect_uri: window.location.origin + '/auth/spacescout/callback',
        state,
        code_challenge: challenge,
        code_challenge_method: 'S256',
        // Force SpaceScout to re-authenticate so SSO does not reuse a sticky MSS session.
        prompt: 'login',
      });
      window.location.href = this.spacescoutAuthorizeUrl + '?' + params.toString();
    });
  }

  private generateCodeVerifier(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return btoa(String.fromCharCode(...array))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }

  private async generateCodeChallenge(verifier: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return btoa(String.fromCharCode(...new Uint8Array(digest)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }

  closeAlert() {
    this.showAlert = false;
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
